import { useQuery } from '@tanstack/react-query';

import { useApi } from './api';
import type { ApiResponse } from '../types';

/**
 * Générations en cours, suivies depuis n'importe quel écran.
 *
 * Une génération dure plusieurs minutes et se déroule côté serveur : elle
 * continue si l'on ferme la fenêtre qui l'a lancée, et même si l'on quitte
 * l'application. Encore faut-il pouvoir le voir sans rouvrir cette fenêtre,
 * d'où ce suivi, posé dans la liste des avant-ventes.
 *
 * L'avancement et le libellé des étapes viennent du serveur, pas d'ici : deux
 * écrans qui les déduiraient chacun de leur côté finiraient par annoncer des
 * choses différentes.
 */

export interface ActiveRun {
    runId: string;
    proposalId: string;
    opportunityId: string;
    title: string | null;
    status: string;
    step: string | null;
    /** Ce qu'on affiche de l'étape en cours, en français. */
    label: string;
    /** Avancement entre 0 et 1. */
    progress: number;
    /** Temps écoulé depuis le démarrage, en secondes, calculé par le serveur. */
    elapsedSeconds: number;
    /**
     * Vrai quand l'exécution dépasse son propre plafond de durée.
     *
     * Elle n'aboutira alors pas : le lanceur la reprend ou la déclare en échec.
     * Le dire évite d'attendre devant une barre qui tourne pour rien, ce qui
     * est arrivé après un redéploiement fait pendant une génération.
     */
    stalled: boolean;
    startedAt: string | null;
    createdAt: string;
}

/**
 * Interroge le serveur tant qu'une génération tourne, et se tait ensuite.
 *
 * L'intervalle est court parce qu'une étape dure quelques dizaines de
 * secondes ; il s'annule dès que plus rien ne tourne, pour ne pas laisser un
 * écran ouvert interroger l'API indéfiniment.
 */
export function useActiveRuns() {
    const api = useApi();
    const { data } = useQuery({
        queryKey: ['proposal-active-runs'],
        queryFn: () => api.get<ApiResponse<ActiveRun[]>>('/api/proposals/active'),
        refetchInterval: (query) => ((query.state.data?.data?.length ?? 0) > 0 ? 5000 : 20000),
        staleTime: 0,
    });

    const runs = data?.data || [];
    const parAvantVente = new Map<string, ActiveRun>();
    for (const run of runs) {
        // Une avant-vente peut porter plusieurs propositions : on montre la
        // plus avancée, celle qui aboutira la première.
        const connue = parAvantVente.get(run.opportunityId);
        if (!connue || run.progress > connue.progress) parAvantVente.set(run.opportunityId, run);
    }
    return { runs, parAvantVente };
}
